import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AffiliateShell from "@/components/feature/AffiliateShell";
import MemberAvatar from "@/components/feature/MemberAvatar";
import { deltaPercent, type Kpi } from "@/services/affiliate";
import { FitImage } from "@/components/feature/EventMedia";
import { fetchEvents, formatDateLong, formatTimeRange, isUpcoming, eventImage, KIND_META, type CalEvent } from "@/services/events";
import {
  activityText,
  exportTeamCsv,
  fetchLeaderDashboard,
  STATE_LABELS,
  type ActivityLevel,
  type LeaderDashboard,
  type MemberState,
  type TeamMember,
} from "@/services/leader";
import { timeAgo } from "@/lib/timeAgo";

const PURPLE = "#7C3AED";
const GREEN = "#20C997";
const AMBER = "#F59E0B";

const fmt = (n: number) => n.toLocaleString("es");

type Filter = "all" | "leads" | "inactive" | "nuevos";

const FILTER_LABELS: Record<Exclude<Filter, "all">, string> = {
  leads: "Con leads calientes",
  inactive: "Afiliados inactivos",
  nuevos: "Afiliados nuevos",
};

const LEVEL_COLOR: Record<ActivityLevel, string> = {
  alta: "#10B981",
  media: "#F59E0B",
  baja: "#F97316",
  inactiva: "#CBD5E1",
};

const STATE_STYLE: Record<MemberState, string> = {
  activo: "bg-emerald-50 text-emerald-700",
  crecimiento: "bg-amber-50 text-amber-700",
  nuevo: "bg-violet-50 text-violet-700",
  inactivo: "bg-slate-100 text-slate-500",
};

function parseDay(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function rangeLabel(start: string, end: string): string {
  const a = parseDay(start);
  const b = parseDay(end);
  const month = b.toLocaleDateString("es", { month: "long" });
  const sameMonth = a.getMonth() === b.getMonth();
  return sameMonth
    ? `${a.getDate()} – ${b.getDate()} de ${month}, ${b.getFullYear()}`
    : `${a.getDate()} de ${a.toLocaleDateString("es", { month: "short" }).replace(".", "")} – ${b.getDate()} de ${month}, ${b.getFullYear()}`;
}

function dayShort(iso: string, long: boolean): string {
  const d = parseDay(iso);
  if (long) return `${d.getDate()}/${d.getMonth() + 1}`;
  const s = d.toLocaleDateString("es", { weekday: "short" }).replace(".", "");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <section className={`bg-white border border-slate-200 rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${className}`}>{children}</section>;
}

function Delta({ kpi, suffix = "vs. semana pasada" }: { kpi: { cur: number; prev: number }; suffix?: string }) {
  const d = deltaPercent({ total: 0, cur: kpi.cur, prev: kpi.prev } as Kpi);
  if (d === null) return <span className="text-[11px] text-slate-400">Sin datos previos</span>;
  const up = d >= 0;
  return (
    <span className={`text-[11px] font-semibold ${up ? "text-emerald-600" : "text-rose-500"}`}>
      <i className={up ? "ri-arrow-up-line" : "ri-arrow-down-line"}></i> {up ? "+" : ""}
      {d}% {suffix}
    </span>
  );
}

function ActivityBars({ member }: { member: TeamMember }) {
  return (
    <span className="inline-flex items-end gap-[3px]" aria-label={`Actividad: ${member.level}`}>
      {member.recent.map((on, i) => (
        <span key={i} className="w-[5px] h-4 rounded-sm" style={{ background: on ? LEVEL_COLOR[member.level === "inactiva" ? "baja" : member.level] : "#E2E8F0" }}></span>
      ))}
    </span>
  );
}

function TeamChart({ series, long }: { series: LeaderDashboard["series"]; long: boolean }) {
  const W = 340;
  const H = 170;
  const padL = 26;
  const padR = 8;
  const padT = 10;
  const padB = 22;
  const maxVal = Math.max(4, ...series.flatMap((s) => [s.prospects, s.leads, s.sales]));
  const step = maxVal <= 8 ? 2 : maxVal <= 20 ? 5 : maxVal <= 40 ? 10 : 20;
  const top = Math.ceil(maxVal / step) * step;
  const x = (i: number) => padL + (i * (W - padL - padR)) / Math.max(1, series.length - 1);
  const y = (v: number) => padT + (1 - v / top) * (H - padT - padB);
  const path = (key: "prospects" | "leads" | "sales") => series.map((s, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(s[key]).toFixed(1)}`).join(" ");
  const ticks: number[] = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  const everyLabel = series.length > 10 ? Math.ceil(series.length / 6) : 1;
  const lines: { key: "prospects" | "leads" | "sales"; color: string }[] = [
    { key: "prospects", color: PURPLE },
    { key: "leads", color: GREEN },
    { key: "sales", color: AMBER },
  ];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Rendimiento del equipo: prospectos, leads y ventas por día">
      <defs>
        <linearGradient id="lid-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={PURPLE} stopOpacity="0.18" />
          <stop offset="100%" stopColor={PURPLE} stopOpacity="0" />
        </linearGradient>
      </defs>
      {ticks.map((v) => (
        <g key={v}>
          <line x1={padL} x2={W - padR} y1={y(v)} y2={y(v)} stroke="#E2E8F0" strokeDasharray="3 3" />
          <text x={padL - 6} y={y(v) + 3} textAnchor="end" fontSize="9" fill="#94A3B8">
            {v}
          </text>
        </g>
      ))}
      <path d={`${path("prospects")} L${x(series.length - 1)},${y(0)} L${x(0)},${y(0)} Z`} fill="url(#lid-grad)" />
      {lines.map((l) => (
        <g key={l.key}>
          <path d={path(l.key)} fill="none" stroke={l.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          {series.length <= 10
            ? series.map((s, i) => <circle key={i} cx={x(i)} cy={y(s[l.key])} r="2.6" fill="#fff" stroke={l.color} strokeWidth="1.5" />)
            : null}
        </g>
      ))}
      {series.map((s, i) =>
        i % everyLabel === 0 ? (
          <text key={s.day} x={x(i)} y={H - 6} textAnchor="middle" fontSize="9" fill="#94A3B8">
            {dayShort(s.day, long)}
          </text>
        ) : null,
      )}
    </svg>
  );
}

export default function Lider() {
  const [data, setData] = useState<LeaderDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [days, setDays] = useState(7);
  const [filter, setFilter] = useState<Filter>("all");
  const [showAll, setShowAll] = useState(false);
  const [meetings, setMeetings] = useState<CalEvent[]>([]);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2600);
  };

  const load = useCallback(async (offset: number, d: number) => {
    try {
      setData(await fetchLeaderDashboard(offset, d));
      setError(false);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    load(weekOffset, days).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [load, weekOffset, days]);

  useEffect(() => {
    let active = true;
    fetchEvents()
      .then((m) => {
        if (active) setMeetings(m);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  const upcoming = useMemo(() => meetings.filter((m) => isUpcoming(m)), [meetings]);
  const meetingsThisWeek = useMemo(() => {
    const limit = Date.now() + 7 * 24 * 3600 * 1000;
    return upcoming.filter((m) => new Date(m.startsAt).getTime() <= limit).length;
  }, [upcoming]);

  const members = useMemo(() => {
    if (!data) return [];
    let list = data.members;
    if (filter === "leads") list = [...list].sort((a, b) => b.leads - a.leads).filter((m) => m.leads > 0);
    if (filter === "inactive") list = list.filter((m) => m.state === "inactivo");
    if (filter === "nuevos") list = list.filter((m) => m.state === "nuevo");
    return list;
  }, [data, filter]);

  const applyFilter = (f: Filter) => {
    setFilter(f);
    setShowAll(true);
    window.setTimeout(() => document.getElementById("equipo-directo")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  if (loading && !data) {
    return (
      <AffiliateShell>
        <div className="py-24 text-center text-slate-500">
          <i className="ri-loader-4-line animate-spin text-3xl"></i>
          <p className="mt-2 text-sm">Cargando tu panel de líder…</p>
        </div>
      </AffiliateShell>
    );
  }

  if (error || !data) {
    return (
      <AffiliateShell>
        <div className="py-24 text-center text-slate-600">
          <i className="ri-error-warning-line text-3xl text-rose-500"></i>
          <p className="mt-2 text-sm">No pudimos cargar tu panel de líder.</p>
          <button onClick={() => load(weekOffset, days)} className="mt-3 px-4 py-2 rounded-lg bg-[#7C3AED] text-white text-sm font-semibold cursor-pointer">
            Reintentar
          </button>
        </div>
      </AffiliateShell>
    );
  }

  const k = data.kpis;
  const first = (data.me?.fullName ?? "").trim().split(/\s+/)[0] || "Líder";
  const shown = showAll || filter !== "all" ? members : members.slice(0, 6);
  const cards: { label: string; icon: string; tint: string; kpi: Kpi; link: string; filter: Filter | "equipo" | "reuniones" }[] = [
    { label: "Afiliados activos", icon: "ri-group-line", tint: "bg-violet-50 text-violet-600", kpi: k.active, link: "Ver equipo", filter: "all" },
    { label: "Prospectos totales", icon: "ri-user-search-line", tint: "bg-sky-50 text-sky-600", kpi: k.prospects, link: "Ver prospectos", filter: "all" },
    { label: "Leads calientes", icon: "ri-filter-3-line", tint: "bg-emerald-50 text-emerald-600", kpi: k.leads, link: "Ver leads", filter: "leads" },
    { label: "Nuevos esta semana", icon: "ri-star-line", tint: "bg-amber-50 text-amber-500", kpi: k.newMembers, link: "Ver nuevos", filter: "nuevos" },
    { label: "Ventas del equipo", icon: "ri-trophy-line", tint: "bg-violet-50 text-violet-600", kpi: k.sales, link: "Ver ventas", filter: "all" },
  ];

  const alerts = [
    {
      icon: "ri-fire-line",
      tint: "bg-orange-50 text-orange-500",
      title: `${data.alerts.hotNoFollowup} lead${data.alerts.hotNoFollowup === 1 ? "" : "s"} caliente${data.alerts.hotNoFollowup === 1 ? "" : "s"} sin seguimiento`,
      sub: "Llevan más de 24 h sin actividad",
      count: data.alerts.hotNoFollowup,
      onClick: () => applyFilter("leads"),
    },
    {
      icon: "ri-time-line",
      tint: "bg-sky-50 text-sky-600",
      title: `${data.alerts.inactive} afiliado${data.alerts.inactive === 1 ? "" : "s"} inactivo${data.alerts.inactive === 1 ? "" : "s"}`,
      sub: "Requieren tu apoyo y seguimiento",
      count: data.alerts.inactive,
      onClick: () => applyFilter("inactive"),
    },
    {
      icon: "ri-user-add-line",
      tint: "bg-violet-50 text-violet-600",
      title: `${data.alerts.newMembers} nuevo${data.alerts.newMembers === 1 ? "" : "s"} afiliado${data.alerts.newMembers === 1 ? "" : "s"}`,
      sub: "Dale la bienvenida y guíalo",
      count: data.alerts.newMembers,
      onClick: () => applyFilter("nuevos"),
    },
    {
      icon: "ri-calendar-event-line",
      tint: "bg-indigo-50 text-indigo-600",
      title: `${meetingsThisWeek} reunion${meetingsThisWeek === 1 ? "" : "es"} del equipo`,
      sub: "Programadas en los próximos 7 días",
      count: meetingsThisWeek,
      to: "/app/reuniones",
    },
  ];
  const alertTotal = alerts.reduce((s, a) => s + a.count, 0);

  const totals = data.seriesTotals;
  const long = days > 7;

  return (
    <AffiliateShell>
      <div className="max-w-[1180px] mx-auto flex flex-col gap-4">
        {/* Encabezado */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div>
            <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-[#0B1F3A] flex items-center gap-2">
              <i className="ri-vip-crown-2-fill text-[#7C3AED]"></i> Panel de Líder
            </h1>
            <p className="text-sm text-slate-500 mt-1">Hola, {first}. Lidera, apoya y haz crecer a tu equipo con propósito.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="relative inline-flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 cursor-pointer">
              <i className="ri-calendar-2-line text-slate-500"></i>
              <span>{rangeLabel(data.weekStart, data.weekEnd)}</span>
              <i className="ri-arrow-down-s-line"></i>
              <select
                value={weekOffset}
                onChange={(e) => setWeekOffset(Number(e.target.value))}
                aria-label="Semana"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              >
                <option value={0}>Esta semana</option>
                <option value={-1}>Semana pasada</option>
                <option value={-2}>Hace 2 semanas</option>
              </select>
            </label>
            <button
              onClick={() => {
                exportTeamCsv(data);
                showToast("Reporte descargado");
              }}
              className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 hover:border-violet-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-2-line"></i> Exportar reporte
            </button>
          </div>
        </div>

        {data.teamSize === 0 ? (
          <div className="rounded-2xl border border-violet-200 bg-violet-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <i className="ri-team-line text-2xl text-violet-600"></i>
            <p className="text-sm text-slate-700 flex-1">
              Aún no tienes afiliados en tu equipo directo. Cuando alguien que invitaste se convierta en socio, aparecerá aquí con su actividad.
            </p>
            <Link to="/app/mi-link" className="px-4 py-2 rounded-lg bg-[#7C3AED] text-white text-xs font-semibold text-center cursor-pointer whitespace-nowrap">
              Compartir mi link
            </Link>
          </div>
        ) : null}

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {cards.map((c) => (
            <Card key={c.label} className="p-4">
              <div className="flex items-center gap-2">
                <span className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg ${c.tint}`}>
                  <i className={c.icon}></i>
                </span>
                <span className="text-xs font-semibold text-slate-600 leading-tight">{c.label}</span>
              </div>
              <div className="mt-2 font-heading font-extrabold text-3xl text-[#0B1F3A]">{fmt(c.kpi.total)}</div>
              <Delta kpi={c.kpi} />
              <button
                onClick={() => (c.filter === "all" ? applyFilter("all") : applyFilter(c.filter as Filter))}
                className="mt-2 block text-xs font-semibold text-[#7C3AED] hover:underline cursor-pointer"
              >
                {c.link}
              </button>
            </Card>
          ))}
        </div>

        {/* Equipo + alertas */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <Card className="lg:col-span-8 p-5" >
            <div id="equipo-directo" className="scroll-mt-24"></div>
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-heading font-bold text-slate-900">Mi equipo directo ({data.teamSize})</h2>
              {data.members.length > 6 && filter === "all" ? (
                <button onClick={() => setShowAll((v) => !v)} className="text-xs font-semibold text-[#7C3AED] cursor-pointer">
                  {showAll ? "Ver menos" : "Ver todo mi equipo"} <i className="ri-arrow-right-line"></i>
                </button>
              ) : null}
            </div>
            {filter !== "all" ? (
              <div className="mt-2 inline-flex items-center gap-2 bg-violet-50 text-violet-700 rounded-full pl-3 pr-1.5 py-1 text-xs font-semibold">
                Filtro: {FILTER_LABELS[filter]}
                <button onClick={() => setFilter("all")} aria-label="Quitar filtro" className="w-5 h-5 rounded-full hover:bg-violet-100 cursor-pointer">
                  <i className="ri-close-line"></i>
                </button>
              </div>
            ) : null}
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm min-w-[520px]">
                <thead>
                  <tr className="text-[11px] text-slate-500 bg-slate-50">
                    <th className="text-left font-semibold px-3 py-2 rounded-l-lg">Afiliado</th>
                    <th className="text-left font-semibold px-2 py-2">Estado</th>
                    <th className="text-center font-semibold px-2 py-2">Prospectos</th>
                    <th className="text-center font-semibold px-2 py-2">Leads</th>
                    <th className="text-center font-semibold px-2 py-2">Ventas</th>
                    <th className="text-left font-semibold px-2 py-2 rounded-r-lg">Actividad</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-slate-500 text-sm py-8">
                        {data.teamSize === 0 ? "Todavía no hay afiliados en tu equipo." : "Ningún afiliado coincide con este filtro."}
                      </td>
                    </tr>
                  ) : (
                    shown.map((m) => (
                      <tr key={m.id} className="border-b border-slate-100 last:border-0">
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2.5">
                            <MemberAvatar name={m.fullName ?? "?"} src={m.avatarUrl} size="sm" />
                            <span className="font-semibold text-slate-900 text-[13px]">{m.fullName ?? "Sin nombre"}</span>
                          </div>
                        </td>
                        <td className="px-2 py-2.5">
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap ${STATE_STYLE[m.state]}`}>{STATE_LABELS[m.state]}</span>
                        </td>
                        <td className="px-2 py-2.5 text-center font-semibold text-slate-800">{m.prospects}</td>
                        <td className="px-2 py-2.5 text-center font-semibold text-slate-800">{m.leads}</td>
                        <td className="px-2 py-2.5 text-center font-semibold text-slate-800">{m.sales}</td>
                        <td className="px-2 py-2.5">
                          <ActivityBars member={m} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
              Nivel de actividad:
              {[
                { l: "Alta", c: LEVEL_COLOR.alta },
                { l: "Media", c: LEVEL_COLOR.media },
                { l: "Baja", c: LEVEL_COLOR.baja },
                { l: "Inactiva", c: LEVEL_COLOR.inactiva },
              ].map((x) => (
                <span key={x.l} className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: x.c }}></span>
                  {x.l}
                </span>
              ))}
            </div>
          </Card>

          <Card className="lg:col-span-4 p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-slate-900 flex items-center gap-2">
                Alertas de seguimiento
                {alertTotal > 0 ? <span className="min-w-5 h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center">{alertTotal}</span> : null}
              </h2>
            </div>
            <ul className="mt-3 flex flex-col">
              {alerts.map((a) => {
                const body = (
                  <>
                    <span className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${a.tint}`}>
                      <i className={a.icon}></i>
                    </span>
                    <span className="flex-1 min-w-0 text-left">
                      <span className="block text-[13px] font-bold text-slate-900">{a.title}</span>
                      <span className="block text-[11px] text-slate-500">{a.sub}</span>
                    </span>
                    <i className="ri-arrow-right-s-line text-slate-400 text-lg"></i>
                  </>
                );
                return (
                  <li key={a.title} className="border-b border-slate-100 last:border-0">
                    {"to" in a && a.to ? (
                      <Link to={a.to} className="flex items-center gap-3 py-3 cursor-pointer">
                        {body}
                      </Link>
                    ) : (
                      <button onClick={a.onClick} className="w-full flex items-center gap-3 py-3 cursor-pointer">
                        {body}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </Card>
        </div>

        {/* Ranking, rendimiento y reuniones/actividad */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <Card className="lg:col-span-3 p-5">
            <h2 className="font-heading font-bold text-slate-900">Ranking de líderes</h2>
            <ul className="mt-3 flex flex-col gap-1">
              {data.ranking.map((r) => (
                <li key={r.id} className={`flex items-center gap-2.5 rounded-xl px-2 py-2 ${r.isMe ? "bg-violet-50" : ""}`}>
                  <span
                    className={`w-5 text-center text-xs font-extrabold ${r.rank === 1 ? "text-amber-500" : r.rank === 2 ? "text-slate-400" : r.rank === 3 ? "text-orange-500" : "text-slate-500"}`}
                  >
                    {r.rank <= 3 ? <i className="ri-medal-fill text-base"></i> : r.rank}
                  </span>
                  <MemberAvatar name={r.fullName ?? "?"} src={r.avatarUrl} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-bold text-slate-900 truncate">
                      {r.fullName ?? "Sin nombre"}
                      {r.isMe ? " (Tú)" : ""}
                    </div>
                    <div className="text-[10px] text-slate-500 truncate">{r.archetype ?? "Líder Vívelo"}</div>
                  </div>
                  <span className="text-[11px] font-bold text-[#7C3AED] whitespace-nowrap">{fmt(r.points)} pts</span>
                </li>
              ))}
            </ul>
            <Link to="/app/afiliado" className="mt-3 block text-xs font-semibold text-[#7C3AED] text-center cursor-pointer">
              Ver mis puntos y niveles <i className="ri-arrow-right-line"></i>
            </Link>
          </Card>

          <Card className="lg:col-span-5 p-5">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h2 className="font-heading font-bold text-slate-900">Rendimiento del equipo</h2>
                <p className="text-[11px] text-slate-500">Últimos {days} días</p>
              </div>
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                aria-label="Periodo"
                className="text-xs font-semibold border border-violet-200 text-violet-700 bg-violet-50 rounded-lg px-2.5 py-1.5 cursor-pointer outline-none"
              >
                <option value={7}>7 días</option>
                <option value={30}>Este mes (30 d)</option>
              </select>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-slate-600">
              {[
                { l: "Prospectos", c: PURPLE },
                { l: "Leads", c: GREEN },
                { l: "Ventas", c: AMBER },
              ].map((x) => (
                <span key={x.l} className="inline-flex items-center gap-1.5">
                  <span className="w-2.5 h-0.5 rounded" style={{ background: x.c }}></span>
                  {x.l}
                </span>
              ))}
            </div>
            <div className="mt-2">
              <TeamChart series={data.series} long={long} />
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 border-t border-slate-100 pt-3">
              {[
                { l: "Prospectos", icon: "ri-user-search-line", t: totals.prospects, c: "text-violet-600" },
                { l: "Leads", icon: "ri-star-line", t: totals.leads, c: "text-emerald-600" },
                { l: "Ventas", icon: "ri-star-fill", t: totals.sales, c: "text-amber-500" },
              ].map((s) => (
                <div key={s.l}>
                  <div className={`text-[11px] font-semibold flex items-center gap-1 ${s.c}`}>
                    <i className={s.icon}></i>
                    {s.l}
                  </div>
                  <div className="font-heading font-extrabold text-xl text-slate-900 leading-tight">{fmt(s.t.cur)}</div>
                  <Delta kpi={s.t} suffix="" />
                </div>
              ))}
            </div>
          </Card>

          <div className="lg:col-span-4 flex flex-col gap-4">
            <Card className="p-5">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-bold text-slate-900">Reuniones del equipo</h2>
                <Link to="/app/reuniones" className="text-xs font-semibold text-[#7C3AED] cursor-pointer">
                  Ver calendario <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              {upcoming.length === 0 ? (
                <p className="mt-3 text-sm text-slate-500">No hay reuniones programadas por ahora.</p>
              ) : (
                <ul className="mt-3 flex flex-col gap-3">
                  {upcoming.slice(0, 2).map((m) => (
                    <li key={m.id} className="flex items-center gap-3">
                      {eventImage(m) ? (
                        <span className="relative w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-slate-100">
                          <FitImage event={m} />
                        </span>
                      ) : (
                        <span className={`w-14 h-14 rounded-xl bg-gradient-to-br ${KIND_META[m.kind].gradient} text-white flex items-center justify-center flex-shrink-0`}>
                          <i className={`${KIND_META[m.kind].icon} text-2xl`}></i>
                        </span>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-bold text-slate-900 truncate">{m.title}</div>
                        <div className="text-[11px] text-slate-500 truncate">
                          <i className="ri-calendar-line"></i> {formatDateLong(m.startsAt)}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">
                          <i className="ri-time-line"></i> {formatTimeRange(m)}
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(`${m.title} · ${formatDateLong(m.startsAt)}, ${formatTimeRange(m)}${m.joinUrl ? ` · ${m.joinUrl}` : ""}`);
                            showToast("Datos de la reunión copiados");
                          } catch {
                            showToast("No se pudo copiar.");
                          }
                        }}
                        className="px-3 py-1.5 rounded-lg border border-violet-300 text-[#7C3AED] text-xs font-semibold cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-share-line mr-1"></i>Compartir
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card className="p-5">
              <h2 className="font-heading font-bold text-slate-900">Actividad reciente</h2>
              {data.activity.length === 0 ? (
                <p className="mt-3 text-sm text-slate-500">Cuando tu equipo avance, verás aquí su actividad.</p>
              ) : (
                <ul className="mt-3 flex flex-col gap-2.5">
                  {data.activity.map((a, i) => {
                    const t = activityText(a.kind);
                    return (
                      <li key={`${a.uid}-${a.kind}-${i}`} className="flex items-center gap-2.5">
                        <MemberAvatar name={a.fullName ?? "?"} src={a.avatarUrl} size="sm" />
                        <div className="flex-1 min-w-0">
                          <div className="text-[12px] text-slate-800 truncate">
                            <span className="font-bold">{a.fullName ?? "Un afiliado"}</span> {t.text}
                          </div>
                          <div className="text-[10px] text-slate-400">{timeAgo(a.at)}</div>
                        </div>
                        <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm flex-shrink-0 ${t.tint}`}>
                          <i className={t.icon}></i>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </div>
        </div>

        {/* Acción recomendada */}
        <div className="rounded-2xl border border-violet-200 bg-violet-50/70 p-4 flex flex-col md:flex-row md:items-center gap-3">
          <span className="w-12 h-12 rounded-full bg-[#7C3AED] text-white flex items-center justify-center text-2xl flex-shrink-0">
            <i className="ri-focus-3-line"></i>
          </span>
          <div className="flex-1">
            <div className="font-heading font-bold text-slate-900 text-sm">Acción recomendada para ti</div>
            <p className="text-xs text-slate-600 mt-0.5">
              {data.alerts.hotNoFollowup > 0
                ? `Tienes ${data.alerts.hotNoFollowup} lead${data.alerts.hotNoFollowup === 1 ? "" : "s"} caliente${data.alerts.hotNoFollowup === 1 ? "" : "s"} sin seguimiento. Conéctate con ${data.alerts.hotNoFollowup === 1 ? "tu equipo" : "tu equipo"} y aumenta las posibilidades de cerrar más ventas.`
                : data.alerts.inactive > 0
                  ? `Tienes ${data.alerts.inactive} afiliado${data.alerts.inactive === 1 ? "" : "s"} inactivo${data.alerts.inactive === 1 ? "" : "s"}. Escríbeles hoy y acompáñalos a retomar el ritmo.`
                  : "Tu equipo va al día. Comparte tu link para seguir sumando personas."}
            </p>
          </div>
          {data.alerts.hotNoFollowup > 0 ? (
            <button onClick={() => applyFilter("leads")} className="px-5 py-2.5 rounded-lg bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm font-semibold cursor-pointer whitespace-nowrap">
              Ver leads calientes <i className="ri-arrow-right-line ml-1"></i>
            </button>
          ) : data.alerts.inactive > 0 ? (
            <button onClick={() => applyFilter("inactive")} className="px-5 py-2.5 rounded-lg bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm font-semibold cursor-pointer whitespace-nowrap">
              Ver inactivos <i className="ri-arrow-right-line ml-1"></i>
            </button>
          ) : (
            <Link to="/app/mi-link" className="px-5 py-2.5 rounded-lg bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm font-semibold cursor-pointer whitespace-nowrap">
              Compartir mi link <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          )}
        </div>
      </div>

      {toast ? <div className="fixed bottom-24 lg:bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-[#0B1F3A] text-white text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg">{toast}</div> : null}
    </AffiliateShell>
  );
}
